import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { useAlerts } from '../../contexts/AlertContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import { EditButton, DeleteButton } from '../../components/permissions/PermissionGuard'
import apiService from '../../services/api'
import {
  HiOutlineArrowLeft,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineEnvelope,
  HiOutlinePhone,
  HiOutlineGlobeAmericas,
  HiOutlineCalendar,
  HiOutlineFolder,
  HiOutlineDocumentText,
  HiOutlineChatBubbleLeft,
  HiOutlineEye,
  HiOutlineUserGroup,
} from 'react-icons/hi2'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import DocumentUploadModal from '../../components/documents/DocumentUploadModal'
import DocumentTable from '../../components/documents/DocumentTable'
import MessagePanel from '../../components/messages/MessagePanel'
import {
  Avatar,
  Badge,
  Banner,
  Button,
  Card,
  EmptyState,
  IconButton,
  Spinner,
} from '../../components/ui'

export default function ClientDetailPage() {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const { isWorkspaceSwitched, ownerAccountId } = useAuth()
  const { showSuccess, showError } = useAlerts()
  const { getEntity } = useWorkspace()

  const [client, setClient] = useState(null)
  const [loading, setLoading] = useState(true)
  const [relatedData, setRelatedData] = useState({ cases: [], sessions: [] })
  const [casesLoading, setCasesLoading] = useState(false)
  const [sessionsLoading, setSessionsLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('overview')
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false, title: '', message: '', onConfirm: null, type: 'warning',
  })
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showMessagePanel, setShowMessagePanel] = useState(false)

  useEffect(() => {
    fetchClient()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, ownerAccountId])

  useEffect(() => {
    if (!client) return
    if (activeTab === 'overview' && relatedData.sessions.length === 0) fetchSessions()
    else if (activeTab === 'cases' && relatedData.cases.length === 0) fetchCases()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, client])

  async function fetchClient() {
    try {
      setLoading(true)
      const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
      const response = await apiService.get(`/clients/${id}/`, { params })
      if (response.success) {
        setClient(response.data.data || response.data)
      } else {
        showError(response.error?.message || t('clients.deleteFailed', { entity: getEntity('client').singular.toLowerCase() }))
        navigate('/clients')
      }
    } catch {
      showError(t('clients.deleteFailed', { entity: getEntity('client').singular.toLowerCase() }))
      navigate('/clients')
    } finally {
      setLoading(false)
    }
  }

  async function fetchCases() {
    if (casesLoading) return
    try {
      setCasesLoading(true)
      const params = isWorkspaceSwitched && ownerAccountId
        ? { owner_account_id: ownerAccountId, client_id: id }
        : { client_id: id }
      const response = await apiService.get('/cases/', { params })
      if (response.success) {
        const data = response.data.data || response.data
        setRelatedData(prev => ({ ...prev, cases: data.data || data.results || [] }))
      }
    } catch { /* swallow */ } finally {
      setCasesLoading(false)
    }
  }

  async function fetchSessions() {
    if (sessionsLoading) return
    try {
      setSessionsLoading(true)
      const params = isWorkspaceSwitched && ownerAccountId
        ? { owner_account_id: ownerAccountId, client: id }
        : { client: id }
      const response = await apiService.get('/sessions/', { params })
      if (response.success) {
        const data = response.data.data || response.data
        setRelatedData(prev => ({ ...prev, sessions: data.data || data.results || [] }))
      }
    } catch { /* swallow */ } finally {
      setSessionsLoading(false)
    }
  }

  const handleDeleteClient = async () => {
    try {
      setConfirmDialog({ isOpen: false })
      const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
      const response = await apiService.delete(`/clients/${id}/`, { params })
      if (response.success) {
        showSuccess(t('clients.deleteSuccess', { entity: getEntity('client').singular }))
        navigate('/clients')
      } else {
        showError(response.error?.message || t('clients.deleteFailed', { entity: getEntity('client').singular.toLowerCase() }))
      }
    } catch {
      showError(t('clients.deleteFailed', { entity: getEntity('client').singular.toLowerCase() }))
    }
  }

  const confirmDeleteClient = () => {
    setConfirmDialog({
      isOpen: true,
      title: t('clients.deleteTitle', { entity: getEntity('client').singular }),
      message: t('clients.deleteMessage', { name: client?.name }),
      type: 'danger',
      confirmText: t('clients.deleteTitle', { entity: getEntity('client').singular }),
      onConfirm: handleDeleteClient,
    })
  }

  const formatDate = (s) => new Date(s).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit',
  })

  const tabs = [
    { id: 'overview',  label: t('clients.detail.tabOverview'),                                          icon: HiOutlineEye,          count: null },
    { id: 'cases',     label: t('clients.detail.tabCases',     { entity: getEntity('case').plural }),     icon: HiOutlineFolder,       count: client?.cases_count || 0 },
    { id: 'documents', label: t('clients.detail.tabDocuments', { entity: getEntity('document').plural }), icon: HiOutlineDocumentText, count: client?.documents_count || 0 },
  ]

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-muted rounded-lg animate-pulse" />
          <div className="h-7 bg-muted rounded w-1/3 animate-pulse" />
        </div>
        <Card padding="md">
          <div className="flex items-center gap-4 mb-5">
            <div className="w-16 h-16 bg-muted rounded-full animate-pulse" />
            <div className="flex-1 space-y-2">
              <div className="h-5 bg-muted rounded w-48 animate-pulse" />
              <div className="h-3 bg-muted rounded w-32 animate-pulse" />
            </div>
          </div>
          <div className="h-32 bg-muted rounded-lg animate-pulse" />
        </Card>
        <div className="flex justify-center"><Spinner /></div>
      </div>
    )
  }

  if (!client) {
    return (
      <Card padding="lg">
        <EmptyState
          icon={HiOutlineUserGroup}
          title={t('clients.detail.notFound', { entity: getEntity('client').singular })}
          description={t('cases.detail.notFoundDescription', { entity: getEntity('client').singular.toLowerCase() })}
          action={
            <Button leadingIcon={HiOutlineArrowLeft} onClick={() => navigate('/clients')}>
              {t('cases.detail.backTo', { entity: getEntity('client').plural })}
            </Button>
          }
        />
      </Card>
    )
  }

  return (
    <div className={`space-y-6 transition-all duration-300 ${showMessagePanel ? 'me-0 sm:me-[400px] md:me-[420px] lg:me-[450px] xl:me-[480px]' : ''}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <IconButton
            icon={HiOutlineArrowLeft}
            aria-label={t('cases.detail.backTo', { entity: getEntity('client').plural })}
            variant="ghost"
            onClick={() => navigate('/clients')}
            className="rtl:rotate-180"
          />
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-semibold text-ink-primary truncate">{client.name}</h1>
            <p className="text-sm text-ink-muted mt-0.5">
              {t('clients.detail.subtitle', { entity: getEntity('client').singular })}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant={showMessagePanel ? 'primary' : 'secondary'}
            leadingIcon={HiOutlineChatBubbleLeft}
            onClick={() => setShowMessagePanel(v => !v)}
          >
            {t('nav.messages')}
          </Button>
          <EditButton resource="client">
            <Button variant="secondary" leadingIcon={HiOutlinePencil} onClick={() => navigate(`/clients/${id}/edit`)}>
              {t('common.edit')}
            </Button>
          </EditButton>
          <DeleteButton resource="client">
            <Button variant="secondary" leadingIcon={HiOutlineTrash} onClick={confirmDeleteClient} className="!text-danger hover:!bg-danger-soft hover:!border-danger/40">
              {t('common.delete')}
            </Button>
          </DeleteButton>
        </div>
      </div>

      <Card padding="md">
        <div className="flex items-center gap-4 mb-5">
          <Avatar name={client.name} size="xl" />
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-ink-primary truncate">{client.name}</h2>
            <p className="text-sm text-ink-muted">
              {t('clients.detail.since', {
                entity: getEntity('client').singular,
                month: new Date(client.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long' }),
              })}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <Section title={t('clients.detail.sectionContact')}>
            <ContactRow
              icon={HiOutlineEnvelope}
              value={client.email}
              href={client.email ? `mailto:${client.email}` : null}
              empty={t('clients.detail.noEmail')}
            />
            <ContactRow
              icon={HiOutlinePhone}
              value={client.mobile}
              href={client.mobile ? `tel:${client.mobile}` : null}
              empty={t('clients.detail.noMobile')}
            />
            <ContactRow
              icon={HiOutlineGlobeAmericas}
              value={typeof client.country === 'object' ? client.country?.name : client.country}
              empty={t('clients.detail.noCountry')}
            />
          </Section>

          <Section title={t('clients.detail.sectionStatistics')}>
            <StatRow label={getEntity('case').plural}     value={client?.cases_count || 0} />
            <StatRow label={getEntity('session').plural}  value={client?.sessions_count || 0} />
            <StatRow label={getEntity('document').plural} value={client?.documents_count || 0} />
          </Section>

          <Section title={t('clients.detail.sectionRecentActivity')}>
            {relatedData.sessions.length > 0 ? (
              <div className="text-sm">
                <p className="text-ink-muted">{t('clients.detail.lastSession', { entity: getEntity('session').singular.toLowerCase() })}</p>
                <p className="font-semibold text-ink-primary mt-0.5">
                  {formatDate(relatedData.sessions[0].created_at)}
                </p>
              </div>
            ) : (
              <p className="text-sm text-ink-muted">{t('clients.detail.noRecentSessions', { entity: getEntity('session').plural.toLowerCase() })}</p>
            )}
          </Section>

          <Section title={t('clients.detail.sectionAccount')}>
            <div className="flex items-center gap-2 text-sm">
              <HiOutlineCalendar className="w-4 h-4 text-ink-muted" />
              <span className="text-ink-secondary">{t('clients.detail.addedOn', { date: formatDate(client.created_at) })}</span>
            </div>
          </Section>
        </div>
      </Card>

      <Card padding="none">
        <nav className="flex gap-1 px-3 sm:px-5 border-b border-border-subtle overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon
            const active = activeTab === tab.id
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`py-3 px-3 -mb-px border-b-2 font-medium text-sm inline-flex items-center gap-2 whitespace-nowrap transition-colors ${
                  active
                    ? 'border-brand text-brand'
                    : 'border-transparent text-ink-secondary hover:text-ink-primary hover:border-border-strong'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {tab.count !== null && (
                  <Badge tone={active ? 'brand' : 'neutral'} size="sm">{tab.count}</Badge>
                )}
              </button>
            )
          })}
        </nav>

        <div className="p-4 sm:p-6">
          {activeTab === 'overview' && (
            <div className="space-y-4">
              <h3 className="text-base font-semibold text-ink-primary">
                {t('clients.detail.overviewHeading', { entity: getEntity('client').singular })}
              </h3>
              <p className="text-sm text-ink-secondary leading-relaxed">
                {t('clients.detail.overviewBody', {
                  name: client.name,
                  entity: getEntity('client').singular.toLowerCase(),
                  caseCount: client?.cases_count || 0,
                  caseEntity: getEntity('case').singular.toLowerCase(),
                  sessionCount: client?.sessions_count || 0,
                  sessionEntity: getEntity('session').singular.toLowerCase(),
                })}
              </p>

              {(!client?.cases_count) && (!client?.sessions_count) && (
                <Banner tone="warning">
                  {t('clients.detail.noCasesYetBanner', {
                    entity: getEntity('client').singular.toLowerCase(),
                    cases: getEntity('case').plural.toLowerCase(),
                    sessions: getEntity('session').plural.toLowerCase(),
                    caseEntity: getEntity('case').singular.toLowerCase(),
                  })}
                </Banner>
              )}
            </div>
          )}

          {activeTab === 'cases' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-base font-semibold text-ink-primary">
                  {t('clients.detail.casesHeading', {
                    clientEntity: getEntity('client').singular,
                    caseEntity: getEntity('case').plural,
                  })}
                </h3>
                <Button leadingIcon={HiOutlineFolder} onClick={() => navigate(`/cases/create?client=${id}`)}>
                  {t('clients.detail.addCase', { entity: getEntity('case').singular })}
                </Button>
              </div>

              {casesLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="border border-border rounded-lg p-4">
                      <div className="w-48 h-4 bg-muted rounded animate-pulse mb-2" />
                      <div className="w-32 h-3 bg-muted rounded animate-pulse" />
                    </div>
                  ))}
                </div>
              ) : relatedData.cases.length === 0 ? (
                <EmptyState
                  icon={HiOutlineFolder}
                  title={t('clients.detail.noCasesFound', { entity: getEntity('case').plural.toLowerCase() })}
                  description={t('clients.detail.noCasesDescription', {
                    entity: getEntity('case').plural.toLowerCase(),
                    client: getEntity('client').singular.toLowerCase(),
                  })}
                  action={
                    <Button onClick={() => navigate(`/cases/create?client=${id}`)}>
                      {t('clients.detail.createFirstCase', { entity: getEntity('case').singular.toLowerCase() })}
                    </Button>
                  }
                  compact
                />
              ) : (
                <ul className="space-y-2">
                  {relatedData.cases.map((c) => (
                    <li
                      key={c.id}
                      className="border border-border rounded-lg p-4 hover:border-border-strong hover:bg-muted/30 transition-colors flex items-center justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <h4 className="text-sm font-semibold text-ink-primary truncate">{c.title}</h4>
                        <p className="text-xs text-ink-muted mt-0.5">{t('cases.created', { date: formatDate(c.created_at) })}</p>
                        {c.description && (
                          <p className="text-xs text-ink-muted mt-1 line-clamp-1">{c.description}</p>
                        )}
                      </div>
                      <Button variant="secondary" size="sm" onClick={() => navigate(`/cases/${c.id}`)}>
                        {t('clients.detail.viewCase', { entity: getEntity('case').singular.toLowerCase() })}
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {activeTab === 'documents' && (
            <DocumentTable
              context="client"
              contextId={parseInt(id)}
              contextName={client?.name}
              onDocumentUpdate={fetchClient}
              uploadModalProps={{ clientId: parseInt(id) }}
            />
          )}
        </div>
      </Card>

      <MessagePanel
        isOpen={showMessagePanel}
        onClose={() => setShowMessagePanel(false)}
        entityType="client"
        entityId={parseInt(id)}
        entityName={client?.name}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
      />

      <DocumentUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadSuccess={() => { fetchClient(); setShowUploadModal(false) }}
        preselectedClient={id}
      />
    </div>
  )
}

function Section({ title, children }) {
  return (
    <div className="space-y-2.5">
      <h3 className="text-xs font-semibold text-ink-muted uppercase tracking-wider">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  )
}

function ContactRow({ icon: Icon, value, href, empty }) {
  if (!value) {
    return (
      <div className="flex items-center gap-2 text-sm text-ink-muted">
        {Icon && <Icon className="w-4 h-4 text-ink-disabled" />}
        {empty}
      </div>
    )
  }
  return (
    <div className="flex items-center gap-2 text-sm">
      {Icon && <Icon className="w-4 h-4 text-ink-muted" />}
      {href ? (
        <a href={href} className="text-brand hover:text-brand-hover truncate">{value}</a>
      ) : (
        <span className="text-ink-secondary truncate">{value}</span>
      )}
    </div>
  )
}

function StatRow({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-ink-secondary">{label}</span>
      <span className="font-semibold text-ink-primary">{value}</span>
    </div>
  )
}
